import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Layers,
  Search,
  BookOpen,
  Users,
  FileText,
  Radio,
  ChevronLeft,
  ChevronRight,
  X,
  Globe,
  EyeOff,
  BarChart3,
} from "lucide-react";
import {
  getCourses,
  updateCourse,
  deleteCourse,
  uploadCourseThumbnail,
  formatCourseLevels,
  JLPT_COURSE_LEVELS,
  type Course,
} from "@/api/lms/courses";
import { examConfigApi } from "@/api/admin/examConfig";
import { getCourseExams } from "@/api/lms/exams";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";

function formatPrice(price: string | undefined | null): string {
  if (!price) return "0";
  const n = Number.parseFloat(price);
  if (Number.isNaN(n)) return price;
  return n.toFixed(2);
}

// ─── Course Card ───────────────────────────────────────────────────────────────
function CourseCard({
  course,
  onEdit,
  onDelete,
  onManage,
}: {
  course: Course;
  onEdit: (c: Course) => void;
  onDelete: (c: Course) => void;
  onManage: (c: Course) => void;
}) {
  const levelLabel = formatCourseLevels(course);

  return (
    <div className="group relative flex flex-col bg-card border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-200">
      {/* Thumbnail */}
      <div className="relative w-full aspect-[16/9] bg-gradient-to-br from-primary/5 to-secondary overflow-hidden">
        {course.thumbnail ? (
          <img
            src={course.thumbnail}
            alt={course.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <BookOpen className="text-primary/30" size={40} />
          </div>
        )}
        {/* Status badge over thumbnail */}
        <div className="absolute top-3 left-3">
          <span className={cn(
            "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold shadow-sm",
            course.isPublished
              ? "bg-emerald-500/90 text-white"
              : "bg-amber-500/90 text-white"
          )}>
            {course.isPublished ? <Globe size={10} /> : <EyeOff size={10} />}
            {course.isPublished ? "Published" : "Draft"}
          </span>
        </div>
        {/* Price badge */}
        {course.price && parseFloat(course.price) > 0 && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center bg-foreground/80 text-background text-[11px] font-bold px-2 py-0.5 rounded-full shadow-sm">
              ৳{formatPrice(course.price)}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-4">
        {/* Level + Type chips */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {levelLabel !== "-" && (
            <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full uppercase tracking-wide">
              {levelLabel}
            </span>
          )}
          <span className={cn(
            "text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide",
            course.type === "LIVE"
              ? "bg-violet-100 text-violet-700"
              : "bg-blue-50 text-blue-600"
          )}>
            {course.type === "LIVE" ? "Live" : "Recorded"}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-foreground text-sm leading-snug line-clamp-2 mb-3 flex-1">
          {course.title}
        </h3>

        {/* Stats row */}
        <div className="flex items-center gap-4 text-[11px] text-muted-foreground mb-4 border-t border-border/60 pt-3">
          <span className="flex items-center gap-1">
            <Users size={11} />
            <span>{course.enrollmentCount ?? 0} students</span>
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="flex-1 h-8 text-xs font-medium"
            onClick={() => onManage(course)}
          >
            <Layers size={13} className="mr-1.5" />
            Build Course
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0 border-border"
            onClick={() => onEdit(course)}
            title="Edit course info"
          >
            <Pencil size={13} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
            onClick={() => onDelete(course)}
            title="Delete course"
          >
            <Trash2 size={13} />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminCoursesPage() {
  useAuth();

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [level, setLevel] = useState("");
  const [type, setType] = useState("");

  const [editing, setEditing] = useState<Course | null>(null);
  const [deleteConfirmCourse, setDeleteConfirmCourse] = useState<Course | null>(null);
  const [draft, setDraft] = useState<{
    title: string;
    description: string;
    levels: string[];
    type: "LIVE" | "RECORDED";
    price: string;
    isPublished: boolean;
  } | null>(null);

  const [thumbnailToSave, setThumbnailToSave] = useState<string | undefined>(undefined);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleLevelChange = (val: string) => {
    setLevel(val);
    setPage(1);
  };

  const handleTypeChange = (val: string) => {
    setType(val);
    setPage(1);
  };

  const coursesQuery = useQuery({
    queryKey: ["lmsCourses", page, limit, search, level, type],
    queryFn: () => getCourses({ page, limit, search, level, type }),
  });

  const { data: activeConfigs = [] } = useQuery({
    queryKey: ["activeExamConfigs"],
    queryFn: () => examConfigApi.getActive(),
  });

  const levelOptions = useMemo(() => {
    return activeConfigs.flatMap(c => 
      (c.levels || []).map(lvl => lvl.code)
    );
  }, [activeConfigs]);

  const closeDialog = () => {
    setEditing(null);
    setDraft(null);
    setThumbnailToSave(undefined);
    setThumbnailUploading(false);
  };

  const openDialog = (c: Course) => {
    setEditing(c);
    setDraft({
      title: c.title ?? "",
      description: (c.description ?? "") as string,
      levels:
        c.levels?.length ? [...c.levels] : c.level ? [c.level] : [],
      type: c.type,
      price: c.price ?? "0",
      isPublished: c.isPublished,
    });
    setThumbnailToSave(undefined);
    setThumbnailUploading(false);
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editing || !draft) return;
      const priceNum = Number.parseFloat(draft.price);
      const payload = {
        title: draft.title.trim(),
        description: draft.description.trim() || undefined,
        levels: draft.levels.length ? draft.levels : [],
        type: draft.type,
        price: Number.isNaN(priceNum) ? undefined : priceNum,
        isPublished: draft.isPublished,
        ...(thumbnailToSave ? { thumbnail: thumbnailToSave } : {}),
      };
      return updateCourse(editing.id, payload);
    },
    onSuccess: () => {
      toast.success("Course updated");
      queryClient.invalidateQueries({ queryKey: ["lmsCourses"] });
      closeDialog();
    },
    onError: (err) => toast.error((err as any)?.message || "Failed to update course"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (courseId: string) => {
      await deleteCourse(courseId);
    },
    onSuccess: () => {
      toast.success("Course deleted");
      queryClient.invalidateQueries({ queryKey: ["lmsCourses"] });
    },
    onError: (err) => toast.error((err as any)?.message || "Failed to delete course"),
  });

  const canEdit = useMemo(() => Boolean(editing && draft), [editing, draft]);

  const courses = coursesQuery.data?.items ?? [];
  const total = coursesQuery.data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  // Summary stats
  const publishedCount = courses.filter(c => c.isPublished).length;
  const draftCount = courses.filter(c => !c.isPublished).length;
  const totalEnrollments = courses.reduce((sum, c) => sum + (c.enrollmentCount ?? 0), 0);

  const hasActiveFilters = searchInput || level || type;

  return (
    <DashboardLayout>
      <div className="dash-page">
        {/* ── Page Header ─────────────────────────────────────────────────── */}
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">Courses</h1>
            <p className="dash-page-subtitle">
              Manage, publish, and build course content.
            </p>
          </div>
          <Button
            onClick={() => navigate("/admin/create-course")}
            className="gap-2 shrink-0"
            size="default"
          >
            <Plus size={16} />
            Create Course
          </Button>
        </div>

        {/* ── Summary Stats ────────────────────────────────────────────────── */}
        {!coursesQuery.isLoading && total > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Total Courses", value: total, icon: BookOpen, color: "text-primary bg-primary/10" },
              { label: "Published", value: publishedCount, icon: Globe, color: "text-emerald-600 bg-emerald-50" },
              { label: "Drafts", value: draftCount, icon: EyeOff, color: "text-amber-600 bg-amber-50" },
              { label: "Total Students", value: totalEnrollments, icon: Users, color: "text-blue-600 bg-blue-50" },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-3 bg-card border border-border/60 rounded-xl p-3 shadow-sm">
                <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", stat.color)}>
                  <stat.icon size={16} />
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground leading-none">{stat.value}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Search + Filters ──────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={15} />
            <Input
              type="text"
              placeholder="Search courses by title or description..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              className="h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:ring-2 focus:ring-ring outline-none min-w-[110px]"
              value={level}
              onChange={(e) => handleLevelChange(e.target.value)}
            >
              <option value="">All Levels</option>
              {levelOptions.map((lvl) => (
                <option key={lvl} value={lvl}>{lvl}</option>
              ))}
            </select>
            <select
              className="h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:ring-2 focus:ring-ring outline-none min-w-[110px]"
              value={type}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="LIVE">Live</option>
              <option value="RECORDED">Recorded</option>
            </select>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 gap-1.5 text-muted-foreground"
                onClick={() => {
                  setSearchInput("");
                  setSearch("");
                  setLevel("");
                  setType("");
                  setPage(1);
                }}
              >
                <X size={13} />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* ── Course Grid ───────────────────────────────────────────────────── */}
        {coursesQuery.isPending ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl overflow-hidden animate-pulse">
                <div className="w-full aspect-[16/9] bg-muted" />
                <div className="p-4 space-y-3">
                  <div className="h-3 bg-muted rounded w-2/3" />
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-4/5" />
                  <div className="h-8 bg-muted rounded-lg w-full mt-4" />
                </div>
              </div>
            ))}
          </div>
        ) : coursesQuery.isError ? (
          <div className="dash-empty-state">
            <p className="text-destructive font-medium">Failed to load courses</p>
            <p className="text-sm text-muted-foreground mt-1">Please try again or contact support.</p>
            <Button variant="outline" className="mt-4" onClick={() => coursesQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : courses.length === 0 ? (
          <div className="dash-empty-state">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
              <BookOpen className="text-primary" size={28} />
            </div>
            <h3 className="font-semibold text-foreground text-lg mb-2">
              {hasActiveFilters ? "No courses match your filters" : "No courses yet"}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {hasActiveFilters
                ? "Try adjusting your search or filters."
                : "Create your first course to get started."}
            </p>
            {!hasActiveFilters ? (
              <Button onClick={() => navigate("/admin/create-course")} className="gap-2">
                <Plus size={16} />
                Create First Course
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchInput("");
                  setSearch("");
                  setLevel("");
                  setType("");
                  setPage(1);
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {courses.map((c) => (
              <CourseCard
                key={c.id}
                course={c}
                onEdit={openDialog}
                onDelete={setDeleteConfirmCourse}
                onManage={(course) => navigate(`/admin/courses/${course.id}/structure`)}
              />
            ))}
          </div>
        )}

        {/* ── Pagination ─────────────────────────────────────────────────────── */}
        {total > limit && (
          <div className="flex items-center justify-between gap-4 mt-6 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Showing {Math.min((page - 1) * limit + 1, total)}–{Math.min(page * limit, total)} of {total} courses
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="gap-1.5"
              >
                <ChevronLeft size={14} />
                Previous
              </Button>
              <span className="text-xs text-muted-foreground px-2">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="gap-1.5"
              >
                Next
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* ── Edit Course Dialog ──────────────────────────────────────────────── */}
        <Dialog open={Boolean(editing)} onOpenChange={(open) => (open ? null : closeDialog())}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Course</DialogTitle>
              <DialogDescription>Update course details and save changes.</DialogDescription>
            </DialogHeader>

            {!draft ? null : (
              <div className="space-y-4">
                {/* Thumbnail */}
                <div className="space-y-2">
                  <Label>Thumbnail</Label>
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 rounded-xl bg-primary/10 overflow-hidden shrink-0 flex items-center justify-center">
                      {thumbnailUploading ? (
                        <Loader2 className="animate-spin text-muted-foreground" size={18} />
                      ) : (
                        ((thumbnailToSave ?? editing?.thumbnail) ? (
                          <img
                            src={thumbnailToSave ?? (editing?.thumbnail ?? "")}
                            alt="Course thumbnail"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">No image</span>
                        ))
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <input
                        ref={thumbnailInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={thumbnailUploading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.currentTarget.value = "";
                          if (!file) return;
                          if (!file.type.startsWith("image/")) {
                            toast.error("Please select an image file");
                            return;
                          }
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error("Thumbnail is too large (max 2MB)");
                            return;
                          }
                          void (async () => {
                            setThumbnailUploading(true);
                            try {
                              const uploaded = await uploadCourseThumbnail(file);
                              setThumbnailToSave(uploaded.url);
                              toast.success("Thumbnail uploaded");
                            } catch (err) {
                              toast.error((err as any)?.message || "Thumbnail upload failed");
                            } finally {
                              setThumbnailUploading(false);
                            }
                          })();
                        }}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => thumbnailInputRef.current?.click()}
                        disabled={thumbnailUploading || updateMutation.isPending}
                      >
                        {thumbnailUploading ? "Uploading..." : "Upload thumbnail"}
                      </Button>
                      <p className="text-xs text-muted-foreground">PNG/JPG up to 2MB</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input value={draft.title} onChange={(e) => setDraft((d) => (d ? { ...d, title: e.target.value } : d))} />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <textarea
                    className="w-full h-24 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground resize-none outline-none focus:ring-2 focus:ring-ring"
                    value={draft.description}
                    onChange={(e) => setDraft((d) => (d ? { ...d, description: e.target.value } : d))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>JLPT Levels</Label>
                  <p className="text-xs text-muted-foreground">Select all levels this course covers.</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {levelOptions.map((code) => (
                      <label key={code} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-border"
                          checked={draft.levels.includes(code)}
                          disabled={updateMutation.isPending}
                          onChange={() => {
                            setDraft((d) => {
                              if (!d) return d;
                              const next = d.levels.includes(code)
                                ? d.levels.filter((x) => x !== code)
                                : [...d.levels, code];
                              return {
                                ...d,
                                levels: next,
                              };
                            });
                          }}
                        />
                        <span>{code}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <select
                      className="h-9 px-3 rounded-lg border border-border bg-background text-sm text-foreground w-full"
                      value={draft.type}
                      onChange={(e) => setDraft((d) => (d ? { ...d, type: e.target.value as any } : d))}
                    >
                      <option value="RECORDED">Recorded</option>
                      <option value="LIVE">Live</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label>Price (BDT)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground select-none">৳</span>
                      <Input
                        value={draft.price}
                        onChange={(e) => setDraft((d) => (d ? { ...d, price: e.target.value } : d))}
                        inputMode="decimal"
                        className="pl-8"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                  <div>
                    <p className="font-medium text-foreground text-sm">Published</p>
                    <p className="text-xs text-muted-foreground">Make visible to students.</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draft.isPublished}
                    onChange={(e) => setDraft((d) => (d ? { ...d, isPublished: e.target.checked } : d))}
                    className="h-4 w-4"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="ghost" onClick={closeDialog} disabled={updateMutation.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!canEdit) return;
                  updateMutation.mutate();
                }}
                disabled={!canEdit || updateMutation.isPending || thumbnailUploading}
              >
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Delete Confirm Dialog ──────────────────────────────────────────── */}
        <Dialog
          open={Boolean(deleteConfirmCourse)}
          onOpenChange={(open) => {
            if (!open) setDeleteConfirmCourse(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete course</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold text-foreground">
                  {deleteConfirmCourse?.title}
                </span>
                ? This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setDeleteConfirmCourse(null)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (!deleteConfirmCourse) return;
                  deleteMutation.mutate(deleteConfirmCourse.id);
                  setDeleteConfirmCourse(null);
                }}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
